import { OPTION_VALUES, PAYMENT_OPTIONS, PREMIUM_MULTIPLE_FACTOR } from "@ipru-common/constants/InputValues";
import {
  APP_KEY_BLOWFISH3,
  IncomeMUltiplier,
  PERSONA_LIST,
  quoteConstant,
  shortCodes,
} from "@ipru-common/constants/SiteConstants";
import { ALL_SA_BANDS } from "@ipru-common/rules/SaBands";
import {
  findSaBandsAndPremium,
  findSalaryRange,
  halfYearlyPremium,
  includeTax,
  monthlyPremium,
} from "./QuoteCommonFunctions";
import {
  cleanAndParseJSON,
  currencyToWord,
  findGreaterThanThreshold,
  isPersonaPage,
  readQueryParams,
  removeHTO,
  replaceText,
} from "./CommonFunctions";
import { getCriticalIllnessPremium } from "./IpsCiPremiumCalculation";
import { findAdbPremium } from "./IpsAdbPremiumCalculation";
import useBlowFish from "@ipru-common/hooks/useBlowFish";
import GlobalStore from "@ipru-common/store/GlobalStore";

export const calculateKFactor = ({ paymentOption, SA }) => {
  let kFactor = 1.0;
  if (paymentOption == "SP") {
    if (SA > 30000000 && SA <= 200000000) {
      kFactor = 1.06;
    } else if (SA > 200000000) {
      kFactor = 1.08;
    }
  } else {
    if (SA > 50000000 && SA <= 200000000) {
      kFactor = 1.06;
    } else if (SA > 200000000) {
      kFactor = 1.08;
    }
  }

  return kFactor;
};

export const findLifeCoverPremium = ({ platform = "ON", formData, rates, paymentOptions }) => {
  const { TRANSGENDER, MALE } = OPTION_VALUES.GENDER;

  let {
    SA,
    age,
    term,
    gender,
    tobacco,
    staff,
    channel,
    payoutType,
    income_duration,
    lumpSumIncomeSingleInst,
    calc_occupation,
    nriGstWaiver = false,
  } = { ...formData, gender: formData?.gender === TRANSGENDER ? MALE : formData?.gender };
  let finalPremium = {};

  // let femaleOfflinePremium = {};

  if (gender && tobacco && rates && paymentOptions.length > 0) {
    paymentOptions.forEach((item) => {
      const paymentOption = item.key;
      if (!paymentOption) {
        return finalPremium;
      }
      const PO = PAYMENT_OPTIONS[paymentOption];

      const dataKey = [platform, shortCodes?.NonSmoker, `${PO.type}${PO.years}`].join("_");

      if (rates?.[dataKey]) {
        const { singlePayMaxYears, saMaxTerms } = quoteConstant;

        const maxTerm = PO.type === OPTION_VALUES.SP ? singlePayMaxYears : saMaxTerms;

        term = PO.type === OPTION_VALUES.SP && term > singlePayMaxYears ? singlePayMaxYears : term;

        const { band, band1, bandSa, band1Sa, bandPremium, band1Premium } = findSaBandsAndPremium({
          SA,
          TERM: term,
          AGE: age,
          rates: rates?.[dataKey],
          maxTerm: PO.type === OPTION_VALUES.WLLP || PO.type === OPTION_VALUES.WL ? term : maxTerm,
          SA_BANDS: ALL_SA_BANDS.LIFE_COVER?.[PO.type] || ALL_SA_BANDS.LIFE_COVER.ALL,
          tobacco,
        });

        let interpolatedValue = 0;
        const kFactor = calculateKFactor({ paymentOption, SA });

        if (band == band1) {
          interpolatedValue = Math.round((SA * bandPremium * kFactor) / bandSa);
        } else {
          interpolatedValue = Math.round(
            bandPremium + (band1Premium - bandPremium) * ((SA - bandSa) / (band1Sa - bandSa))
          );
        }

        const lumpSumPremium = calculateLumpSum({
          staff,
          PPO: paymentOption,
          channel,
          PPT: parseInt(item?.ppt),
          interpolatedValue,
          income_duration,
        });

        const genders = [OPTION_VALUES.GENDER.MALE, OPTION_VALUES.GENDER.FEMALE];

        let illnessPremium = {};

        genders.forEach((gen) => {
          illnessPremium[gen] = getCriticalIllnessPremium({
            formData: {
              ...formData,
              platform,
              gender: gen,
              paymentOption,
              PPT: parseInt(item?.ppt),
            },
          });
        });

        const deathBenefitPremium = findAdbPremium({
          ...formData,
          platform,
          PPT: parseInt(item?.ppt),
          paymentOption,
        });

        const payoutWisePremium = nomineeWiseCalculation({
          payoutType,
          lumpSumPremium,
          income_duration,
          SA,
          lumpSumIncomeSingleInst,
        });

        finalPremium[paymentOption] = payoutWisePremium[gender];
        const { YEARLY, MONTHLY, HALF_YEARLY } = OPTION_VALUES.PAYMENT_TENURE;
        const yearlyPremium = finalPremium[paymentOption][YEARLY];
        [YEARLY, MONTHLY, HALF_YEARLY].forEach((item) => {
          const lifeCoverFinalSummary = addTaxAndDiscount({
            premium: finalPremium[paymentOption][item],
            SA,
            calc_occupation,
            staff,
            paymentOption,
            yearlyPremium,
            item,
            age,
            gender,
            channel,
          });

          finalPremium[paymentOption][item] = createSummary({
            TENURE: item,
            lifeCover: {
              ...lifeCoverFinalSummary,
              maleSecondYearBasePremium: includeTax(payoutWisePremium?.[OPTION_VALUES.GENDER.MALE][item]) + 1,
              maleSecondYearBasePremiumWithoutTax: payoutWisePremium?.[OPTION_VALUES.GENDER.MALE][item],
              maleCi: illnessPremium?.[OPTION_VALUES.GENDER.MALE]?.[item]?.firstYearPremium || 0,
              maleCiWithoutTax: illnessPremium?.[OPTION_VALUES.GENDER.MALE]?.[item]?.withoutTax || 0,
            },
            ci: illnessPremium[gender],
            adb: deathBenefitPremium,
            nriGstWaiver,
          });
        });
      }
    });

    return finalPremium;
  }
};

const nomineeWiseCalculation = ({ payoutType, lumpSumPremium, income_duration, SA, lumpSumIncomeSingleInst }) => {
  const genders = [OPTION_VALUES.GENDER.MALE, OPTION_VALUES.GENDER.FEMALE];

  let result = {};
  genders.forEach((gender) => {
    const annualLumpSum = lumpSumPremium[gender][OPTION_VALUES.PAYMENT_TENURE.YEARLY];

    switch (payoutType) {
      case OPTION_VALUES.LUMP_SUM:
        result[gender] = lumpSumPremium[gender];

        break;
      case OPTION_VALUES.INCOME:
        result[gender] = calculateIncome({
          lumpSumPremium: annualLumpSum,
          income_duration,
        });
        break;
      case OPTION_VALUES.INCREASING_INCOME:
        result[gender] = calculateIncreasingIncome({
          lumpSumPremium: annualLumpSum,
          income_duration,
        });
        break;
      case OPTION_VALUES.LUMP_SUM_AND_INCOME:
        result[gender] = calculateLumpSumAndIncome({
          lumpSumPremium: annualLumpSum,
          income_duration,
          SA,
          lumpSumAmt: lumpSumIncomeSingleInst,
        });
        break;
    }
  });

  return result;
};

const planWiseTotalPremium = ({ lifeCoverFirstYearPremium, ciFirstYearPremium, adbFirstYearPremium }) => {
  return {
    life: lifeCoverFirstYearPremium,
    allInOne: lifeCoverFirstYearPremium + ciFirstYearPremium + adbFirstYearPremium,
    lifeHealth: lifeCoverFirstYearPremium + ciFirstYearPremium,
    lifePlus: lifeCoverFirstYearPremium + adbFirstYearPremium,
  };
};

const createSummary = ({ TENURE, lifeCover, ci, adb, nriGstWaiver }) => {
  let lifeCoverFirstYearPremium = lifeCover?.firstYearPremium;
  let ciFirstYearPremium = ci?.[TENURE]?.firstYearPremium;
  let adbFirstYearPremium = adb?.[TENURE]?.firstYearPremium;

  if (nriGstWaiver) {
    lifeCoverFirstYearPremium = lifeCover?.firstYearPremiumWithoutTax;
    ciFirstYearPremium = ci?.[TENURE]?.withoutTax;
    adbFirstYearPremium = adb?.[TENURE]?.withoutTax;
  }
  return {
    ...lifeCover,
    ciPremiumWithoutTax: ci?.[TENURE]?.withoutTax,
    ciFinalPremium: ci?.[TENURE]?.firstYearPremium,
    adbPremiumWithoutTax: adb?.[TENURE]?.withoutTax,
    adbFinalPremium: adb?.[TENURE]?.firstYearPremium,
    ...planWiseTotalPremium({ lifeCoverFirstYearPremium, ciFirstYearPremium, adbFirstYearPremium }),
  };
};

function calculateLumpSum({ interpolatedValue }) {
  let lumpSumPremium = Math.round(interpolatedValue);
  let femaleLumpSumPremium = Math.round(interpolatedValue * 0.85);

  return {
    [OPTION_VALUES.GENDER.MALE]: {
      [OPTION_VALUES.PAYMENT_TENURE.YEARLY]: lumpSumPremium,
      [OPTION_VALUES.PAYMENT_TENURE.MONTHLY]: monthlyPremium(lumpSumPremium),
      [OPTION_VALUES.PAYMENT_TENURE.HALF_YEARLY]: halfYearlyPremium(lumpSumPremium),
    },
    [OPTION_VALUES.GENDER.FEMALE]: {
      [OPTION_VALUES.PAYMENT_TENURE.YEARLY]: femaleLumpSumPremium,
      [OPTION_VALUES.PAYMENT_TENURE.MONTHLY]: monthlyPremium(femaleLumpSumPremium),
      [OPTION_VALUES.PAYMENT_TENURE.HALF_YEARLY]: halfYearlyPremium(femaleLumpSumPremium),
    },
  };
}

const calculateIncome = ({ lumpSumPremium, income_duration }) => {
  const incomePremium = Math.round(
    lumpSumPremium - (lumpSumPremium * quoteConstant.incomeDurationDiscount[income_duration]) / 100
  );

  return {
    [OPTION_VALUES.PAYMENT_TENURE.YEARLY]: incomePremium,
    [OPTION_VALUES.PAYMENT_TENURE.MONTHLY]: monthlyPremium(incomePremium),
    [OPTION_VALUES.PAYMENT_TENURE.HALF_YEARLY]: halfYearlyPremium(incomePremium),
  };
};

const calculateIncreasingIncome = ({ lumpSumPremium }) => {
  const incomePremium = Math.round(lumpSumPremium * quoteConstant.increasingIncomeMultiValue);

  return {
    [OPTION_VALUES.PAYMENT_TENURE.YEARLY]: incomePremium,
    [OPTION_VALUES.PAYMENT_TENURE.MONTHLY]: monthlyPremium(incomePremium),
    [OPTION_VALUES.PAYMENT_TENURE.HALF_YEARLY]: halfYearlyPremium(incomePremium),
  };
};

const calculateLumpSumAndIncome = ({ lumpSumPremium, SA, income_duration, lumpSumAmt }) => {
  const { incomeDurationDiscount } = quoteConstant;

  // Step 1: Calculate D32
  let D32 = parseInt(lumpSumAmt) / parseInt(SA);
  D32 = Math.round(D32 * 10000) / 10000; // Round to 4 decimal places

  // Step 2: Calculate D33
  var D33 = 1 - D32;

  const discount = incomeDurationDiscount[income_duration] / 100;
  // Step 3: Calculate the final result
  const incomePremium = Math.round((1 - D33 * discount) * lumpSumPremium);

  return {
    [OPTION_VALUES.PAYMENT_TENURE.YEARLY]: incomePremium,
    [OPTION_VALUES.PAYMENT_TENURE.MONTHLY]: monthlyPremium(incomePremium),
    [OPTION_VALUES.PAYMENT_TENURE.HALF_YEARLY]: halfYearlyPremium(incomePremium),
  };
};

export const addTaxAndDiscount = (inputData) => {
  const firstYearPremiumWithoutTax = calculateForFirstYear(inputData);
  const firstYearPremium = includeTax(firstYearPremiumWithoutTax) + 1;

  return {
    withoutTax: inputData?.premium,
    firstYearPremium,
    firstYearPremiumWithoutTax,
    fromSecondYearOnwards: includeTax(inputData?.premium) + 1,
  };
};

function calculateForFirstYear({ staff, calc_occupation, paymentOption, yearlyPremium, item, channel }) {
  const paymentOptionType = PAYMENT_OPTIONS[paymentOption].type;
  let result = yearlyPremium * 1;

  if (staff === OPTION_VALUES.STAFF_Y && [OPTION_VALUES.BOL, OPTION_VALUES.PROPRIETARY].includes(channel)) {
    const { staffDiscountWLRP, staffDiscountWLLP, staffDiscountDefault } = quoteConstant;
    if ([OPTION_VALUES.RP, OPTION_VALUES.WL].includes(paymentOptionType)) {
      result = Math.round(result * staffDiscountWLRP);
    } else if ([OPTION_VALUES.LP, OPTION_VALUES.WLLP].includes(paymentOptionType)) {
      result = Math.round(result * staffDiscountWLLP);
    } else {
      result = Math.round(result * staffDiscountDefault);
    }
  }

  if (calc_occupation === OPTION_VALUES.OCCUPATION.SALARIED) {
    if ([OPTION_VALUES.RP, OPTION_VALUES.WL].includes(paymentOptionType)) {
      result = Math.round(result * quoteConstant.ciNonSpYearlyOthersGt10);
    } else if ([OPTION_VALUES.LP, OPTION_VALUES.WLLP].includes(paymentOptionType)) {
      result = Math.round(result * quoteConstant.wllpCharges);
    }
  }

  if (item === OPTION_VALUES.PAYMENT_TENURE.MONTHLY) {
    return Math.round(monthlyPremium(result));
  } else if (item === OPTION_VALUES.PAYMENT_TENURE.HALF_YEARLY) {
    return Math.round(halfYearlyPremium(result));
  }
  return Math.round(result);
}

export function calculateGTDiscount() {
  const global = GlobalStore.getSnapshot();
  const {
    quoteFormData: { breakupDetail, paymentOption },
  } = global;
  const { base, salariedDiscountAmt, onlineDiscount, femaleDiscount } = breakupDetail || {};
  const { compaionDiscountLp, compaionDiscountRp, compaionDiscountSp } = quoteConstant;
  const premiumWithoutDiscount = base - salariedDiscountAmt - onlineDiscount - femaleDiscount;
  let gtDiscount = 0;
  if (paymentOption === OPTION_VALUES.RP || paymentOption === OPTION_VALUES.WL) {
    gtDiscount = compaionDiscountRp;
  } else if (paymentOption === OPTION_VALUES.SP) {
    gtDiscount = compaionDiscountSp;
  } else {
    gtDiscount = compaionDiscountLp;
  }
  const discount = premiumWithoutDiscount - Math.floor(premiumWithoutDiscount * gtDiscount);
  return { companionDiscount: discount, totalOnlineDiscount: breakupDetail?.totalOnlineDiscount + discount, total: breakupDetail?.total - discount }

}

export const getLumpSumAmtByPayoutWise = (formFields) => {
  const {
    lumpSumAmt,
    lumpSumAndIncomeYears,
    increasingIncomePayoutYears,
    monthlyIncomePayoutYears,
    lumpSumIncomeSingleInst,
  } = formFields;

  return {
    lumpSumAmt,
    monthlyIncomeAmt: getMonthlyIncome({
      lumpSumAmt,
      monthlyIncomePayoutYears,
    }),
    increasingIncomeAmt: getIncreasingIncomeAmt({
      lumpSumAmt,
      increasingIncomePayoutYears,
    }),
    ...getLumpSumAndRegularIncome({
      lumpSumAmt,
      lumpSumAndIncomeYears,
      lumpSumIncomeSingleInst,
    }),
  };
};
export const getLifeCoverFromMonthlyIncome = ({ monthlyIncome, payoutYears }) => {
  return monthlyIncome / IncomeMUltiplier[payoutYears];
};
export const getLifeCoverLumpSumIncome = ({ monthlyIncome, payoutYears }) => {
  const lifeCover = monthlyIncome / IncomeMUltiplier[payoutYears];
  return { lifeCover: lifeCover * 2, lumpSumAmt: lifeCover };
};
export const createMonthlyIncomeDropDown = ({ lifeCoverDropDown, isLumpsumAndIncome, payoutYears }) => {
  let incomeDropDown = [];
  lifeCoverDropDown.forEach((cover) => {
    const income = isLumpsumAndIncome
      ? getMonthlyIncomePersona({ lumpSumAmt: cover.id / 2, monthlyIncomePayoutYears: payoutYears })
      : getMonthlyIncomePersona({ lumpSumAmt: cover.id, monthlyIncomePayoutYears: payoutYears });
    incomeDropDown = [...incomeDropDown, { id: income, name: income }];
  });
  return incomeDropDown;
};
export const getLumsumAmountFromLifeCover = (lifeCover) => Math.round(lifeCover / 2);
export const getMonthlyIncomePersona = ({ lumpSumAmt, monthlyIncomePayoutYears }) =>
  Math.round(lumpSumAmt * IncomeMUltiplier[monthlyIncomePayoutYears]);

export const getMonthlyIncome = ({ lumpSumAmt, monthlyIncomePayoutYears }) =>
  Math.round(lumpSumAmt / (monthlyIncomePayoutYears * 12));

export const getIncreasingIncomeAmt = ({ lumpSumAmt, increasingIncomePayoutYears }) =>
  Math.round(lumpSumAmt / (increasingIncomePayoutYears * 12));

export const getLumpSumAndRegularIncome = ({ lumpSumAmt, lumpSumIncomeSingleInst, lumpSumAndIncomeYears }) => {
  const lumpSumIncomeRegularInst = (lumpSumAmt - lumpSumIncomeSingleInst) / (lumpSumAndIncomeYears * 12);

  // const lumpSumIncomeRegularInst = lumpSumIncomeSingleInst * IncomeMUltiplier[lumpSumAndIncomeYears];
  return { lumpSumIncomeSingleInst, lumpSumIncomeRegularInst };
};

export const createLumpSumIncomeDropdown = (lumpSum) => {
  const halfOfSumpSum = parseInt(lumpSum) / 2;

  const gap = halfOfSumpSum * 0.1;
  let options = [];
  for (let index = gap; index < lumpSum; index = index + gap) {
    options.push({
      id: index,
      name: currencyToWord(index),
    });
  }

  return options;
};
export const createLumpSumQuarterOptions = (lumpSum) => {
  const parsedLumpSum = parseInt(lumpSum, 10);

  const percentages = [0.25, 0.5, 0.75];

  const options = percentages.map((percentage) => {
    const value = Math.round(parsedLumpSum * percentage);
    const percentageText = `${Math.round(percentage * 100)}%`;

    return {
      id: value,
      name: `${currencyToWord(value)} (${percentageText})`,
    };
  });

  return options;
};

// const checkPersonaEmployeeQdata = () => {
//   const queryParams = readQueryParams();
//   const qData = queryParams.get("qdata")
//   const result = decryptEmployeeBlowfishQData(qData.replace(/ /g, "+"))
//   if (result.sumAssured) {
//     return result.sumAssured
//   }

// }

export const checkPersonaEmployeeQdata = () => {
  const { decryptBlowfishQData } = useBlowFish({ key: APP_KEY_BLOWFISH3 });

  const queryParams = readQueryParams();
  const qData = queryParams.get("qdata");
  try {
    if (qData) {
      const result = decryptBlowfishQData(qData.replace(/ /g, "+"));
      const final = cleanAndParseJSON(result);
      if (final?.sumAssured) {
        return final.sumAssured;
      }
    }
    return false;
  } catch (error) {
    console.log(error);
  }
};

export const createLifeCoverDropdown = ({
  yr_income,
  age,
  pasa,
  pasa_amount,
  isNegativePincode,
  wifeDetail,
  min_sa,
  max_sa,
  incomeChanged,
  isMonthlyPersona,
  payoutYears,
  isLumpsumAndIncome,
  calc_occupation,
  spouse_cover = false,
}) => {
  let lastOption = 0;
  let options = [];

  let { recommendedSumAssured: maxSa } = getSumAssuredByIncome({
    income: yr_income,
    age,
    changeIncome: incomeChanged,
    maxSumAssured: max_sa,
  });
  let minSa = quoteConstant.minSumAssured;

  if (calc_occupation === OPTION_VALUES.OCCUPATION.HOUSEWIFE && spouse_cover && max_sa) {
    maxSa = max_sa;
  } else {
    if (max_sa > 0 && max_sa <= maxSa) {
      maxSa = max_sa;
    }

    if (min_sa > 0) {
      minSa = min_sa;
    }

    if (wifeDetail && wifeDetail?.calc_occupation === OPTION_VALUES.OCCUPATION.HOUSEWIFE) {
      if (yr_income >= 300000 && yr_income < 500000) {
        maxSa = quoteConstant.HOUSEWIFE_DEFAULT_LIFE_COVER;
      } else {
        maxSa = quoteConstant.HOUSEWIFE_MAX_LIFE_COVER;
      }
    }
    if (pasa === OPTION_VALUES.YES && pasa_amount) {
      maxSa = parseInt(pasa_amount);
    }
    const personaName = isPersonaPage();
    if (personaName === PERSONA_LIST.EMPLOYEE) {
      let emp_max = parseInt(checkPersonaEmployeeQdata());
      if (emp_max && maxSa > emp_max) {
        maxSa = emp_max;
      }
    }
    for (
      let index = isNegativePincode === OPTION_VALUES.NO ? minSa : quoteConstant.minSumAssuredNegativePinCode;
      index <= maxSa;
      index = index + (index > 7500000 ? 5000000 : 2500000)
    ) {
      lastOption = index;
      if (index > quoteConstant.maxSumAssured) {
        options.push({
          id: quoteConstant.maxSumAssured,
          name: currencyToWord(quoteConstant.maxSumAssured),
          subname: isMonthlyPersona
            ? getMonthlyIncomePersona({
              lumpSumAmt: isLumpsumAndIncome ? quoteConstant.maxSumAssured / 2 : quoteConstant.maxSumAssured,
              monthlyIncomePayoutYears: payoutYears,
            })
            : null,
        });
        break;
      }

      options.push({
        id: index,
        name: currencyToWord(index),
        subname: isMonthlyPersona
          ? getMonthlyIncomePersona({
            lumpSumAmt: isLumpsumAndIncome ? index / 2 : index,
            monthlyIncomePayoutYears: payoutYears,
          })
          : null,
      });
    }

    if (maxSa > lastOption) {
      options.push({
        id: maxSa,
        name: currencyToWord(maxSa),
        subname: isMonthlyPersona
          ? getMonthlyIncomePersona({
            lumpSumAmt: isLumpsumAndIncome ? maxSa / 2 : maxSa,
            monthlyIncomePayoutYears: payoutYears,
          })
          : null,
      });
    }

    return options;
  }
};

export const maximumSumAssuredByIncome = (props) => {
  const { income, age, changeIncome = false } = props;
  const multiplyBy = findGreaterThanThreshold(PREMIUM_MULTIPLE_FACTOR, age);
  const { key, range } = findSalaryRange(income, OPTION_VALUES.ANNUAL_INCOME.RANGE);
  let userIncome = parseInt(income) == key ? range.min : income;

  if (changeIncome) {
    userIncome = income;
  }

  return removeHTO(parseInt(userIncome) * (multiplyBy || quoteConstant.maxSumAssuredMultiple));
};

export const getSumAssuredByIncome = (props) => {
  const { income, age, changeIncome = false, maxSumAssured = 0 } = props;
  const multiplyBy = findGreaterThanThreshold(PREMIUM_MULTIPLE_FACTOR, age);
  const { key, range } = findSalaryRange(income, OPTION_VALUES.ANNUAL_INCOME.RANGE);
  let userIncome = parseInt(income) == key ? range.min : income;

  if (changeIncome) {
    userIncome = income;
  }

  let recommendedSumAssured = removeHTO(parseInt(userIncome) * (multiplyBy || quoteConstant.maxSumAssuredMultiple));

  if (recommendedSumAssured < quoteConstant.minSumAssured) {
    recommendedSumAssured = quoteConstant.minSumAssured;
  }

  recommendedSumAssured =
    recommendedSumAssured > quoteConstant.maxSumAssured ? quoteConstant.maxSumAssured : recommendedSumAssured;
  if (maxSumAssured < recommendedSumAssured && maxSumAssured > 0) {
    recommendedSumAssured = maxSumAssured;
  }
  return {
    recommendedSumAssured,
    userIncome,
  };
};

export const checkSmartExitEligibility = ({ coverTillAge, age, t }) => {
  let result = { eligible: false, message: "" };
  const AGE_26 = 26;
  const end_const = 5;
  const term_31 = 31;

  if (age >= 18 && age <= 54) {
    if (coverTillAge < Math.max(age + term_31, 65)) {
      result = {
        eligible: false,
        message: replaceText(t("quote.smart_exit_note.life_cover_greater"), "[TERM]", Math.max(age + term_31, 65)),
        startAge: "",
        endAge: "",
      };
    } else if (coverTillAge === Math.max(age + term_31, 65)) {
      result = {
        eligible: true,
        message: replaceText(t("quote.smart_exit_note.during"), "[AGE]", Math.max(60, age + AGE_26)),
        startAge: Math.max(60, age + AGE_26).toString(),
        endAge: "",
      };
    } else {
      result = {
        eligible: true,
        message: replaceText(
          t("quote.smart_exit_note.between"),
          "[AGE]",
          `${Math.max(60, age + AGE_26)} - ${coverTillAge - end_const}`
        ),
        startAge: Math.max(60, age + AGE_26).toString(),
        endAge: (coverTillAge - end_const).toString(),
      };
    }
  } else if (age >= 55 && age <= 65) {
    if (coverTillAge < 99) {
      result = {
        eligible: false,
        message: replaceText(t("quote.smart_exit_note.life_cover_greater_99"), "[TERM]", `${99}`),
        startAge: "",
        endAge: "",
      };
    } else if (coverTillAge === 99) {
      result = {
        eligible: true,
        message: replaceText(
          t("quote.smart_exit_note.between"),
          "[AGE]",
          `${Math.max(60, age + AGE_26)} - ${coverTillAge - end_const}`
        ),
        startAge: Math.max(60, age + AGE_26).toString(),
        endAge: (coverTillAge - end_const).toString(),
      };
    }
  }
  return result;
};

// export const checkSmartExitEligibility = ({ coverTillAge, age, t, paymentOption }) => {
//   const ppt1 = [OPTION_VALUES.RP, OPTION_VALUES.WLRP, OPTION_VALUES.WLLP60, OPTION_VALUES.LP60, OPTION_VALUES.LPT5];
//   const ppt2 = [OPTION_VALUES.LP5, OPTION_VALUES.LP7, OPTION_VALUES.LP10, OPTION_VALUES.WLLP10, OPTION_VALUES.LP15];
//   let result = { eligible: false, message: "" };
//   const end_const = 5;

//   if (ppt1.includes(paymentOption)) {
//     if (age >= 18 && age <= 50) {
//       if (coverTillAge < Math.max(age + 35, 65)) {
//         result = {
//           eligible: false,
//           message: replaceText(t("quote.smart_exit_note.life_cover_greater"), "[TERM]", Math.max(age + 35, 65)),
//           premiumRefund: 0,
//         };
//       } else if (coverTillAge === Math.max(age + 35, 65)) {
//         result = {
//           eligible: true,
//           message: replaceText(t("quote.smart_exit_note.during"), "[AGE]", Math.max(60, age + 30)),
//           premiumRefund: 100,
//         };
//       } else if (coverTillAge > Math.max(age + 35, 65)) {
//         result = {
//           eligible: true,
//           message: replaceText(
//             t("quote.smart_exit_note.between"),
//             "[AGE]",
//             `${Math.max(60, age + 30)} - ${coverTillAge - end_const}`
//           ),
//           premiumRefund: 100,
//         };
//       }
//     } else if (age >= 51 && age <= 60) {
//       if (coverTillAge < 99) {
//         result = {
//           eligible: false,
//           message: replaceText(t("quote.smart_exit_note.life_cover_greater_99"), "[TERM]", 99),
//           premiumRefund: 0,
//         };
//       } else if (coverTillAge === 99) {
//         result = {
//           eligible: true,
//           message: replaceText(
//             t("quote.smart_exit_note.between"),
//             "[AGE]",
//             `${Math.max(60, age + 30)} - ${coverTillAge - end_const}`
//           ),
//           premiumRefund: 100,
//         };
//       }
//     }
//   } else if (ppt2.includes(paymentOption)) {
//     if (age >= 18 && age <= 45) {
//       if (coverTillAge < Math.max(age + 35, 65)) {
//         result = {
//           eligible: false,
//           message: replaceText(t("quote.smart_exit_note.life_cover_greater_200"), "[TERM]", Math.max(age + 40, 70)),
//           premiumRefund: 0,
//         };
//       } else if (coverTillAge === Math.max(age + 35, 65)) {
//         result = {
//           eligible: true,
//           message: `${replaceText(
//             t("quote.smart_exit_note.between"),
//             "[AGE]",
//             `${Math.max(60, age + 30)} `
//           )} or <br> ${replaceText(
//             t("quote.smart_exit_note.life_cover_greater_200"),
//             "[TERM]",
//             Math.max(70, age + 40)
//           )}`,
//           premiumRefund: 100,
//         };
//       } else if (coverTillAge > Math.max(age + 35, 65) && coverTillAge < Math.max(age + 40, 70)) {
//         result = {
//           eligible: true,
//           message: `${replaceText(
//             t("quote.smart_exit_note.between"),
//             "[AGE]",
//             `${Math.max(60, age + 30)} - ${coverTillAge - end_const}`
//           )} or <br> ${replaceText(
//             t("quote.smart_exit_note.life_cover_greater_200"),
//             "[TERM]",
//             Math.max(70, age + 40)
//           )}`,
//           premiumRefund: 100,
//         };
//       } else if (coverTillAge === Math.max(age + 40, 70)) {
//         result = {
//           eligible: true,
//           message: `${replaceText(
//             t("quote.smart_exit_note.during_200"),
//             "[AGE]",
//             coverTillAge - 10
//           )} or <br> ${replaceText(
//             t("quote.smart_exit_note.between"),
//             "[AGE]",
//             `${Math.max(60, age + 30) + 1} - ${coverTillAge - end_const}`
//           )}`,
//           premiumRefund: 200,
//         };
//       } else if (coverTillAge > Math.max(age + 40, 70)) {
//         result = {
//           eligible: true,
//           message: `${replaceText(
//             t("quote.smart_exit_note.during_200"),
//             "[AGE]",
//             coverTillAge - 10
//           )} or <br> ${replaceText(
//             t("quote.smart_exit_note.between"),
//             "[AGE]",
//             `${Math.max(60, age + 30)} - ${coverTillAge - 11} years and ${coverTillAge - 9} - ${
//               coverTillAge - end_const
//             }`
//           )}`,
//           premiumRefund: 200,
//         };
//       }
//     } else if (age >= 46 && age <= 50) {
//       if (coverTillAge === 99) {
//         result = {
//           eligible: true,
//           message: `${replaceText(
//             t("quote.smart_exit_note.during_200"),
//             "[AGE]",
//             coverTillAge - 10
//           )} or <br> ${replaceText(
//             t("quote.smart_exit_note.between"),
//             "[AGE]",
//             `${Math.max(60, age + 30)} - ${coverTillAge - 11} years and ${coverTillAge - 9} - ${
//               coverTillAge - end_const
//             }`
//           )}`,
//           premiumRefund: 200,
//         };
//       } else if (coverTillAge === Math.max(age + 35, 65)) {
//         result = {
//           eligible: true,
//           message: `${replaceText(
//             t("quote.smart_exit_note.between"),
//             "[AGE]",
//             `${Math.max(60, age + 30)} `
//           )} or <br> ${replaceText(t("quote.smart_exit_note.life_cover_greater_99_200"), "[TERM]", 99)}`,
//           premiumRefund: 100,
//         };
//       } else if (coverTillAge > Math.max(age + 35, 65)) {
//         result = {
//           eligible: true,
//           message: `${replaceText(
//             t("quote.smart_exit_note.between"),
//             "[AGE]",
//             `${Math.max(60, age + 30)} - ${coverTillAge - end_const}`
//           )} or <br> ${replaceText(t("quote.smart_exit_note.life_cover_greater_99_200"), "[TERM]", 99)}`,
//           premiumRefund: 100,
//         };
//       } else if (coverTillAge < 99) {
//         result = {
//           eligible: false,
//           message: `${replaceText(t("quote.smart_exit_note.life_cover_greater_99_200"), "[TERM]", 99)}`,
//           premiumRefund: 0,
//         };
//       }
//     } else if (age >= 51 && age < 60) {
//       if (coverTillAge < 99) {
//         result = {
//           eligible: false,
//           message: replaceText(t("quote.smart_exit_note.life_cover_greater_99_200"), "[TERM]", 99),
//           premiumRefund: 0,
//         };
//       } else if (coverTillAge === 99) {
//         result = {
//           eligible: true,
//           message: `${replaceText(
//             t("quote.smart_exit_note.during_200"),
//             "[AGE]",
//             coverTillAge - 10
//           )} or <br> ${replaceText(
//             t("quote.smart_exit_note.between"),
//             "[AGE]",
//             `${Math.max(60, age + 30)} - ${coverTillAge - 11} years and ${coverTillAge - 9} - ${
//               coverTillAge - end_const
//             }`
//           )}`,

//           premiumRefund: 200,
//         };
//       }
//     } else if (age === 60) {
//       if (coverTillAge < 99) {
//         result = {
//           eligible: false,
//           message: replaceText(t("quote.smart_exit_note.life_cover_greater_99"), "[TERM]", 99),
//           premiumRefund: 0,
//         };
//       } else if (coverTillAge === 99) {
//         result = {
//           eligible: true,
//           message: replaceText(
//             t("quote.smart_exit_note.between"),
//             "[AGE]",
//             `${Math.max(60, age + 30)} - ${coverTillAge - end_const}`
//           ),
//           premiumRefund: 100,
//         };
//       }
//     }
//   }

//   return result;
// };

const calculateSavings = ({ result, paymentOption, tenure, ppt, nriGstWaiver, adb_selected, ci_selected }) => {
  const { MONTHLY, HALF_YEARLY, YEARLY } = OPTION_VALUES.PAYMENT_TENURE;
  const frequency = {
    [MONTHLY]: 12,
    [YEARLY]: 1,
    [HALF_YEARLY]: 2,
  };

  const lumpSum = result[OPTION_VALUES.LUMP_SUM][paymentOption][tenure];
  const income = result[OPTION_VALUES.INCOME][paymentOption][tenure];
  const lumpSumAndIncome = result[OPTION_VALUES.LUMP_SUM_AND_INCOME][paymentOption][tenure];

  const getSavingValue = (paymentOption) => {
    const withoutGst =
      (adb_selected ? paymentOption?.adbPremiumWithoutTax : 0) + (ci_selected ? paymentOption?.ciPremiumWithoutTax : 0);
    const withGst =
      (adb_selected ? paymentOption?.adbFinalPremium : 0) + (ci_selected ? paymentOption?.ciFinalPremium : 0);
    const result =
      (nriGstWaiver ? paymentOption?.withoutTax + withoutGst : paymentOption?.fromSecondYearOnwards + withGst) *
      parseInt(ppt - 1) *
      frequency[tenure] +
      (nriGstWaiver
        ? paymentOption?.firstYearPremiumWithoutTax + withoutGst
        : paymentOption?.firstYearPremium + withGst) *
      frequency[tenure];
    return result;
  };

  const lumpSumIncome = getSavingValue(lumpSum);
  const monthlySaving = lumpSumIncome - getSavingValue(income);
  const lumpSumAndIncomeSaving = lumpSumIncome - getSavingValue(lumpSumAndIncome);

  return { lumpSumIncome, monthlySaving, lumpSumAndIncomeSaving };
};

export const findPayoutSavings = ({ userInputs, premiumRates, paymentOptions }) => {
  let savings = {
    [OPTION_VALUES.LUMP_SUM]: 0,
    [OPTION_VALUES.LUMP_SUM_AND_INCOME]: 0,
  };

  const { term, SA, CI_SA, ADB_SA, paymentOption, tenure, ppt, nriGstWaiver, ci_selected, adb_selected } = userInputs;
  if (term && SA && CI_SA && ADB_SA && paymentOption) {
    let result = {
      [OPTION_VALUES.LUMP_SUM]: null,
      [OPTION_VALUES.INCOME]: null,
      [OPTION_VALUES.LUMP_SUM_AND_INCOME]: null,
    };

    const opt = [OPTION_VALUES.LUMP_SUM, OPTION_VALUES.INCOME, OPTION_VALUES.LUMP_SUM_AND_INCOME];

    const duration = {
      [OPTION_VALUES.LUMP_SUM]: userInputs.income_duration,
      [OPTION_VALUES.LUMP_SUM_AND_INCOME]: userInputs.lumpSumAndIncomeYears,
      [OPTION_VALUES.INCOME]: userInputs.monthlyIncomePayoutYears,
    };
    const selectedPaymentOption = paymentOptions.filter((item) => item.key === paymentOption);

    opt.forEach((item) => {
      result[item] = findLifeCoverPremium({
        formData: {
          ...userInputs,
          channel: OPTION_VALUES.BOL,
          payoutType: item,
          income_duration: duration[item],
        },
        rates: premiumRates,
        platform: OPTION_VALUES.ONLINE,
        paymentOptions: selectedPaymentOption,
      });
    });

    if (result?.[OPTION_VALUES.LUMP_SUM]) {
      const { lumpSumIncome, monthlySaving, lumpSumAndIncomeSaving } = calculateSavings({
        result,
        paymentOption,
        tenure,
        ppt,
        nriGstWaiver,
        ci_selected,
        adb_selected,
      });

      savings[OPTION_VALUES.LUMP_SUM] = lumpSumIncome;

      savings[OPTION_VALUES.INCOME] = {
        saving: monthlySaving,
        percentage: Math.round((monthlySaving / lumpSumIncome) * 100),
      };
      savings[OPTION_VALUES.LUMP_SUM_AND_INCOME] = {
        saving: lumpSumAndIncomeSaving,
        percentage: Math.round((lumpSumAndIncomeSaving / lumpSumIncome) * 100),
      };
    }
  }
  return savings;
};
